import { useLocation, useNavigate } from "react-router-dom";

import { Button, Tooltip } from "@fronterahealth/frontera-ui-components";

import { LayoutFooter } from "@components/Layout";
import { FormSavingIndicator } from "@components/ReportField/FormSavingIndicator";
import { useHasUnsavedFields } from "@components/ReportField/useHasUnsavedFields";
import { notifyError } from "@components/notifications/notifications";
import { getOrderedAssessmentReportSubRoutes } from "@pages/AssessmentReportV2Details/helpers";
import { InitialAssessmentPageRoute } from "@pages/AssessmentReportV2Details/types";

interface FooterButtonRowProps {
  primaryButtonTitle?: string;
  primaryButtonLoading?: boolean;
  primaryButtonDisabled?: boolean;
  primaryButtonType?: React.ButtonHTMLAttributes<HTMLButtonElement>["type"];
  primaryButtonAction?: () => Promise<unknown>;
  primaryButtonDisabledTooltipReason?: string;
  skipNavigate?: boolean;
  hideBackButton?: boolean;
  dataTestID?: string;
  leadingActions?: React.ReactNode;
}
export const FooterButtonRow: React.FC<FooterButtonRowProps> = ({
  primaryButtonAction,
  primaryButtonType = "button",
  primaryButtonTitle = "Next Step",
  primaryButtonDisabled = false,
  primaryButtonDisabledTooltipReason = "",
  primaryButtonLoading,
  skipNavigate = false,
  hideBackButton = false,
  dataTestID = "footer-primary-button",
  leadingActions,
}) => {
  const { pathname } = useLocation();
  const currentRoute =
    (pathname.split("#")?.pop()?.split("/").pop() as InitialAssessmentPageRoute) ||
    ("upload-files" as InitialAssessmentPageRoute);
  const { getNextRoute, getPastRoute } = getOrderedAssessmentReportSubRoutes();
  const nextRoute = getNextRoute(currentRoute);
  const pastRoute = getPastRoute(currentRoute);

  const hasUnsavedFields = useHasUnsavedFields();
  const go = useNavigate();

  const handlePrimaryButtonAction = async (e?: React.MouseEvent<HTMLElement>) => {
    if (primaryButtonType === "submit") {
      return;
    }

    e?.preventDefault();
    e?.stopPropagation();
    if (nextRoute) {
      window.scrollTo({ top: 0 });
      if (primaryButtonAction) {
        try {
          await primaryButtonAction();
          if (!skipNavigate) {
            go(`../${nextRoute}`);
          }
        } catch (_err) {
          console.error("Error calling primaryButtonAction from FooterButtonRow");
          notifyError("Failed to perform action");
          return;
        }
      } else {
        if (!skipNavigate) {
          window.scrollTo({ top: 0 });
          go(`../${nextRoute}`);
        }
      }
    }
  };

  return (
    <LayoutFooter
      id="btnRow"
      className="flex w-full items-center justify-end border-t border-border-strong px-8 bg-white py-[11.5px]"
    >
      <div className="flex items-center gap-2 flex-1">{leadingActions}</div>
      <div className="flex items-center gap-2">
        <FormSavingIndicator />
        {!hideBackButton && (
          <Button
            appearance="secondary"
            text="Previous Step"
            onClick={() => {
              if (pastRoute) {
                window.scrollTo({ top: 0 });
                go(`../${pastRoute}`);
              }
            }}
          />
        )}
        {primaryButtonDisabled && primaryButtonDisabledTooltipReason ? (
          <Tooltip text={primaryButtonDisabledTooltipReason} showInfoIcon={false}>
            <Button
              dataTestID={dataTestID}
              appearance="primary"
              disabled={primaryButtonDisabled || hasUnsavedFields}
              text={primaryButtonLoading ? "Loading..." : primaryButtonTitle}
              type={primaryButtonType}
              onClick={handlePrimaryButtonAction}
            />
          </Tooltip>
        ) : (
          <Button
            dataTestID={dataTestID}
            appearance="primary"
            disabled={primaryButtonDisabled || hasUnsavedFields}
            text={primaryButtonLoading ? "Loading..." : primaryButtonTitle}
            type={primaryButtonType}
            onClick={handlePrimaryButtonAction}
          />
        )}
      </div>
    </LayoutFooter>
  );
};
